import { z } from 'zod';
import type { Visibility } from '../../domain/entities/common.ts';
import {
  canChoose,
  ChoiceSchema,
  SelectionDeadlineSchema,
  selectionDeadlineDate,
  SelectionSchema,
  SELECTION_STATUS_LABEL,
  selectionStatus,
  type Allowance,
  type Choice,
  type Selection,
  type SelectionStatus,
} from '../../domain/entities/selection.ts';
import { linkKey } from '../../domain/entities/file.ts';
import { RecordRefSchema, type RecordRef } from '../../domain/entities/notification.ts';
import { MoneySchema } from '../../domain/money.ts';
import type { Due } from '../../domain/entities/todo.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { MemberKind } from '../../ports/auth-port.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Selections and allowances.
 *
 * The owner writes to this collection, which nothing else in the product
 * lets them do except answering a schedule confirmation — and for the same
 * reason: the answer is theirs to give, and routing it through a function
 * would mean a function for every question a customer can be asked. The
 * rules narrow what they may touch to the three fields that record a
 * choice, which is the same shape as `isOwnConfirmationOnly`.
 */
export class SelectionError extends Error {
  readonly reason: 'invalid' | 'locked' | 'notChoosable';

  constructor(reason: 'invalid' | 'locked' | 'notChoosable', message: string) {
    super(message);
    this.name = 'SelectionError';
    this.reason = reason;
  }
}

export interface SelectionContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

function repo(context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<Selection>(
    { companyId: context.companyId, jobId: context.jobId },
    'selections',
  );
}

function allowanceRepo(context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<Allowance>(
    { companyId: context.companyId, jobId: context.jobId },
    'allowances',
  );
}

export const SaveSelectionInputSchema = z.object({
  title: z.string().trim().min(1, 'A selection needs a title').max(200),
  description: z.string().max(20_000).optional(),
  category: z.string().trim().max(120).optional(),
  location: z.string().trim().max(120).optional(),
  allowanceId: z.string().min(1).nullish(),
  /**
   * A date or a schedule item to follow (P4-19).
   *
   * `SelectionDeadlineSchema` accepts both, and lifts a bare
   * `YYYY-MM-DD` — which a form posting the old shape still sends, and
   * which every document written before P4-19 carries.
   */
  deadline: SelectionDeadlineSchema.nullish(),
  visibility: z.custom<Visibility>().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
  linkedRecords: z.array(RecordRefSchema).optional(),
});
export type SaveSelectionInput = z.input<typeof SaveSelectionInputSchema>;

/**
 * Both link fields together, so the two cannot be written apart.
 *
 * `linkedKeys` exists only because `array-contains` cannot look inside a
 * map, so it is derived here on every write that touches the links rather
 * than maintained by a caller — a caller that updated one and not the
 * other would leave a selection that is linked and unfindable.
 */
function linkFields(
  records: readonly RecordRef[],
): Pick<Selection, 'linkedRecords' | 'linkedKeys'> {
  return { linkedRecords: [...records], linkedKeys: records.map(linkKey) };
}

export async function createSelection(
  context: SelectionContext,
  input: SaveSelectionInput,
): Promise<Selection> {
  const parsed = SaveSelectionInputSchema.safeParse(input);
  if (!parsed.success) throw new SelectionError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const selection: Selection = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    description: parsed.data.description ?? '',
    ...(parsed.data.category ? { category: parsed.data.category } : {}),
    ...(parsed.data.location ? { location: parsed.data.location } : {}),
    ...(parsed.data.allowanceId ? { allowanceId: parsed.data.allowanceId } : {}),
    ...(parsed.data.deadline ? { deadline: parsed.data.deadline } : {}),
    choices: [],
    released: false,
    locked: false,
    // Owner-visible by default, unlike everything else in this product. A
    // selection the owner cannot see is a question nobody asked, which is
    // the one thing a selection cannot be.
    visibility: parsed.data.visibility ?? {
      internal: true,
      subs: false,
      owner: true,
      private: false,
    },
    tagIds: parsed.data.tagIds ? [...parsed.data.tagIds] : [],
    ...linkFields(parsed.data.linkedRecords ?? []),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = SelectionSchema.safeParse(selection);
  if (!checked.success) throw new SelectionError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(selection);
  return selection;
}

async function load(context: SelectionContext, id: string): Promise<Selection> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('selections', id);
  return existing;
}

async function write(
  context: SelectionContext,
  existing: Selection,
  patch: Partial<Selection>,
): Promise<Selection> {
  const next = {
    ...patch,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'selections',
      id: existing.id,
      patch: next,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...next };
}

/** Refuse to change a locked selection: the order has gone in. */
function assertUnlocked(selection: Selection): void {
  if (selection.locked) {
    throw new SelectionError(
      'locked',
      `“${selection.title}” is locked because it has been ordered. Unlock it first.`,
    );
  }
}

export async function updateSelection(
  context: SelectionContext,
  id: string,
  input: SaveSelectionInput,
): Promise<Selection> {
  const parsed = SaveSelectionInputSchema.safeParse(input);
  if (!parsed.success) throw new SelectionError('invalid', parsed.error.issues[0]!.message);

  const existing = await load(context, id);
  assertUnlocked(existing);

  return write(context, existing, {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    category: parsed.data.category ?? existing.category,
    location: parsed.data.location ?? existing.location,
    allowanceId:
      parsed.data.allowanceId === undefined
        ? existing.allowanceId
        : (parsed.data.allowanceId ?? undefined),
    deadline:
      parsed.data.deadline === undefined ? existing.deadline : (parsed.data.deadline ?? undefined),
    visibility: parsed.data.visibility ?? existing.visibility,
    tagIds: parsed.data.tagIds ? [...parsed.data.tagIds] : existing.tagIds,
    ...(parsed.data.linkedRecords === undefined ? {} : linkFields(parsed.data.linkedRecords)),
  });
}

/**
 * Set or clear a selection's deadline (P4-19b).
 *
 * A narrow action rather than a round trip through `updateSelection`,
 * which is how every other control on the expanded row works — the
 * allowance link and the schedule link both have one. Passing the whole
 * record back to change one field is also how a concurrent edit to the
 * title gets overwritten by whatever the form was holding.
 *
 * Refused on a **locked** selection, like every other edit but the record
 * links: locking means the order has gone in, and a deadline for a choice
 * that can no longer change is a date nobody can act on.
 */
export async function setSelectionDeadline(
  context: SelectionContext,
  id: string,
  deadline: Due | null,
): Promise<Selection> {
  const existing = await load(context, id);
  assertUnlocked(existing);
  return write(context, existing, { deadline: deadline ?? undefined });
}

// ---- links ---------------------------------------------------------------

/**
 * Link a selection to a record. Linking twice is a double click, not an
 * error — the same reading as `linkTodoTo`.
 *
 * Allowed on a **locked** selection, unlike every other edit here. Locking
 * means the order has gone in and the choice cannot change; it does not
 * mean nobody may ever record that the tiles belong to the bathroom phase.
 */
export async function linkSelectionTo(
  context: SelectionContext,
  id: string,
  record: RecordRef,
): Promise<Selection> {
  const existing = await load(context, id);
  const already = existing.linkedRecords.some(
    (link) => link.kind === record.kind && link.id === record.id,
  );
  if (already) return existing;

  return write(context, existing, linkFields([...existing.linkedRecords, record]));
}

export async function unlinkSelectionFrom(
  context: SelectionContext,
  id: string,
  record: RecordRef,
): Promise<Selection> {
  const existing = await load(context, id);
  return write(
    context,
    existing,
    linkFields(
      existing.linkedRecords.filter(
        (link) => !(link.kind === record.kind && link.id === record.id),
      ),
    ),
  );
}

/**
 * Point a selection at one schedule item, or at none.
 *
 * One write, not an unlink followed by a link. Two writes would be two
 * chances to half-apply, and — the failure that produced this function —
 * the second would be computed from whatever the caller was holding, which
 * on a screen is last render's copy. A test that switched the picker twice
 * quickly ended up with a selection linked to both items.
 *
 * Replaces **every** schedule-item link rather than appending, because the
 * feature doc's list has one "Linked Schedule Item" column: a second link
 * would be a state nothing displays. Links to other kinds are untouched.
 */
export async function setLinkedScheduleItem(
  context: SelectionContext,
  id: string,
  itemId: string | null,
): Promise<Selection> {
  const existing = await load(context, id);
  const others = existing.linkedRecords.filter((link) => link.kind !== 'scheduleItem');
  const next: RecordRef[] =
    itemId === null
      ? others
      : [...others, { kind: 'scheduleItem', id: itemId, jobId: existing.jobId }];

  const before = existing.linkedRecords.map(linkKey).join('|');
  const after = next.map(linkKey).join('|');
  // Nothing to do is not a write. Re-choosing the item it already has
  // would otherwise stamp `updatedAt` for no change anybody made.
  if (before === after) return existing;

  return write(context, existing, linkFields(next));
}

/**
 * The selections linked to one record, as a query a rule can approve.
 *
 * Built from `visibleSelectionQueries` rather than written out again, so a
 * screen cannot reach a selection through a link that it could not reach
 * through the list. If the rule's branch for a kind changes, this changes
 * with it.
 */
export function linkedSelectionQueries(
  record: RecordRef,
  kind: MemberKind = 'internal',
): readonly Query[] {
  const key = linkKey(record);
  return visibleSelectionQueries(kind).map((query) => ({
    ...query,
    where: [...(query.where ?? []), { field: 'linkedKeys', op: 'array-contains', value: key }],
  }));
}

export async function listLinkedSelections(
  context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>,
  record: RecordRef,
  kind: MemberKind = 'internal',
): Promise<Selection[]> {
  const page = await repo(context).list(linkedSelectionQueries(record, kind)[0]!);
  return [...page.items];
}

/** The same, live. One query per kind, as for the list. */
export function subscribeToLinkedSelections(
  context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>,
  record: RecordRef,
  onChange: (selections: readonly Selection[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
): Unsubscribe {
  return repo(context).subscribe(
    linkedSelectionQueries(record, kind)[0]!,
    (page) => onChange(page.items),
    onError,
  );
}

export const SaveChoiceInputSchema = z.object({
  title: z.string().trim().min(1, 'A choice needs a title').max(200),
  description: z.string().max(2_000).optional(),
  /** Absent is a real answer: the catalogue has not come back yet. */
  price: MoneySchema.nullish(),
  vendorContactId: z.string().min(1).optional(),
  imageFileId: z.string().min(1).optional(),
});
export type SaveChoiceInput = z.input<typeof SaveChoiceInputSchema>;

export const MAX_CHOICES = 50;

export async function addChoice(
  context: SelectionContext,
  id: string,
  input: SaveChoiceInput,
): Promise<Selection> {
  const parsed = SaveChoiceInputSchema.safeParse(input);
  if (!parsed.success) throw new SelectionError('invalid', parsed.error.issues[0]!.message);

  const existing = await load(context, id);
  assertUnlocked(existing);
  if (existing.choices.length >= MAX_CHOICES) {
    throw new SelectionError(
      'invalid',
      `A selection holds at most ${MAX_CHOICES} choices. More than that is a catalogue, not a decision.`,
    );
  }

  const choice: Choice = {
    id: newId(),
    title: parsed.data.title,
    ...(parsed.data.description ? { description: parsed.data.description } : {}),
    ...(parsed.data.price ? { price: parsed.data.price } : {}),
    ...(parsed.data.vendorContactId ? { vendorContactId: parsed.data.vendorContactId } : {}),
    ...(parsed.data.imageFileId ? { imageFileId: parsed.data.imageFileId } : {}),
  };
  const checked = ChoiceSchema.safeParse(choice);
  if (!checked.success) throw new SelectionError('invalid', checked.error.issues[0]!.message);

  return write(context, existing, { choices: [...existing.choices, choice] });
}

/**
 * Remove a choice.
 *
 * Removing the one the owner chose clears the choice as well, rather than
 * leaving `selectedChoiceId` pointing at nothing. A selection that says
 * "chosen" with nothing chosen is worse than one that says nothing.
 */
export async function removeChoice(
  context: SelectionContext,
  id: string,
  choiceId: string,
): Promise<Selection> {
  const existing = await load(context, id);
  assertUnlocked(existing);

  const wasChosen = existing.selectedChoiceId === choiceId;
  return write(context, existing, {
    choices: existing.choices.filter((choice) => choice.id !== choiceId),
    ...(wasChosen
      ? { selectedChoiceId: undefined, selectedBy: undefined, selectedAt: undefined }
      : {}),
  });
}

/** Show it to the owner, or take it back. */
export async function setReleased(
  context: SelectionContext,
  id: string,
  released: boolean,
): Promise<Selection> {
  const existing = await load(context, id);
  assertUnlocked(existing);
  if (existing.released === released) return existing;
  return write(context, existing, { released });
}

/**
 * Lock it, because the order has gone in.
 *
 * Deliberately available without a choice having been made: an order can go
 * in on a verbal decision, and refusing to lock would leave somebody
 * editing a selection the supplier has already cut tiles for.
 */
export async function setLocked(
  context: SelectionContext,
  id: string,
  locked: boolean,
): Promise<Selection> {
  const existing = await load(context, id);
  if (existing.locked === locked) return existing;
  const now = context.clock.now().toISOString();
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'selections',
      id,
      patch: { locked, updatedAt: now, updatedBy: context.uid },
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, locked, updatedAt: now, updatedBy: context.uid };
}

/**
 * Choose, as the owner or on their behalf.
 *
 * The three fields the rules let an owner write, and nothing else. Recorded
 * with who and when, because "the tiles were chosen" and "Steven chose them
 * on the 14th" answer different questions — and the second is the one that
 * settles an argument about an order.
 */
export async function chooseSelection(
  context: SelectionContext,
  id: string,
  choiceId: string,
): Promise<Selection> {
  const existing = await load(context, id);
  if (!canChoose(existing, choiceId)) {
    throw new SelectionError(
      'notChoosable',
      existing.locked
        ? `“${existing.title}” has been ordered and can no longer be changed.`
        : !existing.released
          ? `“${existing.title}” has not been shared with you yet.`
          : 'That option has no price yet, so it cannot be chosen.',
    );
  }

  const now = context.clock.now().toISOString();
  return write(context, existing, {
    selectedChoiceId: choiceId,
    selectedBy: context.uid,
    selectedAt: now,
  });
}

/** Take the choice back, which is not the same as removing the option. */
export async function clearChoice(context: SelectionContext, id: string): Promise<Selection> {
  const existing = await load(context, id);
  assertUnlocked(existing);
  return write(context, existing, {
    selectedChoiceId: undefined,
    selectedBy: undefined,
    selectedAt: undefined,
  });
}

// ---- allowances ----------------------------------------------------------

export const SaveAllowanceInputSchema = z.object({
  name: z.string().trim().min(1, 'An allowance needs a name').max(200),
  amount: MoneySchema,
  costCodeId: z.string().min(1).optional(),
});
export type SaveAllowanceInput = z.input<typeof SaveAllowanceInputSchema>;

export async function createAllowance(
  context: SelectionContext,
  input: SaveAllowanceInput,
): Promise<Allowance> {
  const parsed = SaveAllowanceInputSchema.safeParse(input);
  if (!parsed.success) throw new SelectionError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const allowance: Allowance = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    name: parsed.data.name,
    amount: parsed.data.amount,
    ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };
  await allowanceRepo(context).create(allowance);
  return allowance;
}

export function subscribeToAllowances(
  context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>,
  onChange: (allowances: readonly Allowance[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return allowanceRepo(context).subscribe(
    { orderBy: [{ field: 'name', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

// ---- reading -------------------------------------------------------------

/**
 * The queries whose union is "the selections on this job you may see".
 *
 * Shaped by member kind, like the daily logs': an owner asks for
 * `visibility.owner == true` plus `released == true`, because an unreleased
 * selection is a question nobody has asked yet and showing it would be the
 * builder thinking aloud in front of a customer.
 */
export function visibleSelectionQueries(kind: MemberKind = 'internal'): readonly Query[] {
  const byTitle = [{ field: 'title', direction: 'asc' as const }];
  if (kind === 'client') {
    return [
      {
        where: [
          { field: 'visibility.owner', op: '==', value: true },
          { field: 'released', op: '==', value: true },
        ],
        orderBy: byTitle,
      },
    ];
  }
  if (kind === 'sub') {
    return [
      {
        where: [
          { field: 'visibility.subs', op: '==', value: true },
          { field: 'released', op: '==', value: true },
        ],
        orderBy: byTitle,
      },
    ];
  }
  return [{ orderBy: byTitle }];
}

export function subscribeToSelections(
  context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>,
  onChange: (selections: readonly Selection[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
): Unsubscribe {
  // One query for every kind, so no merging is needed: a selection has no
  // private audience — a private selection is a question asked of nobody.
  return repo(context).subscribe(
    visibleSelectionQueries(kind)[0]!,
    (page) => onChange(page.items),
    onError,
  );
}

export async function listSelections(
  context: Pick<SelectionContext, 'data' | 'companyId' | 'jobId'>,
  kind: MemberKind = 'internal',
): Promise<Selection[]> {
  const page = await repo(context).list(visibleSelectionQueries(kind)[0]!);
  return [...page.items];
}

export const SELECTION_GROUPINGS = ['location', 'category', 'deadline', 'status'] as const;
export type SelectionGrouping = (typeof SELECTION_GROUPINGS)[number];

export interface SelectionGroup {
  readonly key: string;
  readonly label: string;
  readonly selections: readonly Selection[];
}

/**
 * Grouped as the feature doc's toolbar offers.
 *
 * Anything without the grouping field lands in one bucket at the end rather
 * than being dropped — a selection with no location is still a selection
 * somebody has to make.
 */
export function groupSelections(
  selections: readonly Selection[],
  grouping: SelectionGrouping,
  today: string,
  /**
   * How to turn a selection's deadline into a plain date (P4-19).
   *
   * A deadline that follows a schedule item needs the schedule and the
   * job's working calendar to resolve, and neither belongs in here. The
   * default handles a plain date and reports a schedule-linked one as
   * unresolved, which is the honest answer for a caller that has no
   * schedule; the selections page passes a resolver built on `resolveDue`.
   */
  deadlineFor: (selection: Selection) => string | null = (selection) =>
    selectionDeadlineDate(selection.deadline),
): SelectionGroup[] {
  const groups = new Map<string, Selection[]>();
  const keyOf = (selection: Selection): string => {
    if (grouping === 'location') return selection.location ?? '';
    if (grouping === 'category') return selection.category ?? '';
    if (grouping === 'deadline') return deadlineFor(selection) ?? '';
    return selectionStatus(selection, today, deadlineFor(selection));
  };

  for (const selection of selections) {
    const key = keyOf(selection);
    const existing = groups.get(key);
    if (existing) existing.push(selection);
    else groups.set(key, [selection]);
  }

  const LAST = '￿';
  return [...groups.entries()]
    .sort(([a], [b]) => {
      const left = a === '' ? LAST : a;
      const right = b === '' ? LAST : b;
      return left < right ? -1 : left > right ? 1 : 0;
    })
    .map(([key, group]) => ({
      key: key === '' ? 'none' : key,
      label: labelFor(grouping, key),
      selections: group,
    }));
}

/**
 * What a group is called.
 *
 * Grouping by status shows the **words a reader sees** rather than the key:
 * "Waiting for a choice", not "available". The label table already exists
 * for the badges, and a heading that disagreed with the badge under it would
 * be two names for one state.
 */
function labelFor(grouping: SelectionGrouping, key: string): string {
  if (key === '') {
    if (grouping === 'location') return 'No location';
    if (grouping === 'category') return 'No category';
    if (grouping === 'deadline') return 'No deadline';
    return 'No status';
  }
  if (grouping === 'status') {
    return SELECTION_STATUS_LABEL[key as SelectionStatus] ?? key;
  }
  return key;
}
